import type {FilteredItemsPageUrlQueryParams} from '@frontend/modules/public/ads/AdsModuleTypes'
import {useAdsStore} from '@frontend/stores'
import type {
    MouseEvent,
    SubmitEvent,
} from 'react'
import {matchPath} from 'react-router-dom'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import {useUrlQueryParams} from 'swayok-react-mdb-ui-kit/helpers/useUrlQueryParams'
import {NavigationService} from 'swayok-react-mdb-ui-kit/services/NavigationService'
import type {AnyObject} from 'swayok-react-mdb-ui-kit/types/Common'
import {appUrl} from '../../../../../../AppUrl'
import {modifyItemsSearchUrlQueryParams} from '../../../../../../helpers/modifyItemsSearchUrlQueryParams'

type HookReturnFn = (
    event?: SubmitEvent<HTMLFormElement> | MouseEvent<HTMLElement>,
    filters?: AnyObject
) => void

// Возвращает обработчик события для отправки формы поиска ключевых слов.
// Сюда не нужно передавать keywords т.к. они обновляются через useAdsStore()
export function useItemsSearchKeywordsSubmitHandler(): HookReturnFn {
    const isSearchPanelDisabled = useAdsStore(
        state => state.isSearchPanelDisabled
    )
    const makeItemsListUrl = useAdsStore(
        state => state.makeItemsListUrl
    )
    const setIsSearchPanelDisabled = useAdsStore(
        state => state.setIsSearchPanelDisabled
    )
    // Данные о текущем URL.
    const {
        urlQueryData,
    } = useUrlQueryParams<FilteredItemsPageUrlQueryParams>()

    // Нажатие кнопки "Найти" для ключевых слов.
    return useEventCallback((
        event?: SubmitEvent<HTMLFormElement> | MouseEvent<HTMLElement>,
        filters?: AnyObject
    ): void => {
        event?.preventDefault?.()
        if (isSearchPanelDisabled) {
            return
        }
        // Блокировка повторных запросов на небольшое время, чтобы предотвратить спам.
        setIsSearchPanelDisabled(true)
        setTimeout(() => setIsSearchPanelDisabled(false), 1500)

        // Определяем на какой странице мы сейчас находимся.
        const currentUrl: string = NavigationService.getCurrentPath() ?? window.location.pathname
        const isItemsListPage: boolean = (
            // Поиск объявлений.
            !!matchPath(appUrl().ads.category, currentUrl)
            // Поиск услуг.
            || !!matchPath(appUrl().services.category, currentUrl)
        )

        const baseUrlQueryData = isItemsListPage
            // Модифицируем существующие параметры URL Query.
            ? urlQueryData
            // Собираем новый URL Query.
            : {}
        // Собираем URL query для перехода на страницу объявлений или услуг.
        modifyItemsSearchUrlQueryParams(
            baseUrlQueryData,
            {},
            filters
        )
            .then(entries => {
                const queryParams: Partial<FilteredItemsPageUrlQueryParams> = Object.fromEntries(entries)
                // Удаляем ключевые слова из параметров,
                // чтобы новые добавились в makeItemsListUrl().
                delete queryParams.keywords
                // Переходим на страницу объявлений.
                NavigationService.navigate(makeItemsListUrl(queryParams))
            })
            .catch(e => {
                console.error(e)
                // Переходим на страницу объявлений без применения фильтров.
                delete baseUrlQueryData.keywords
                NavigationService.navigate(makeItemsListUrl(baseUrlQueryData))
            })
    })
}
